import { useState, useEffect } from "react";
import { api } from "@/App";
import { 
  TrendingUp, TrendingDown, DollarSign, Calculator, 
  ChevronLeft, ChevronRight, Car, Wrench, Paintbrush,
  Calendar, Percent, Receipt
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { getWeek } from "date-fns";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

function formatMoney(amount) {
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount || 0);
}

function StatCard({ title, value, subtitle, icon: Icon, color = "slate", trend }) {
  const colorClasses = {
    emerald: "bg-emerald-100 text-emerald-600",
    red: "bg-red-100 text-red-600",
    blue: "bg-blue-100 text-blue-600",
    amber: "bg-amber-100 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
    indigo: "bg-indigo-100 text-indigo-600"
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className={`text-2xl font-bold ${
              trend === "up" ? "text-emerald-600" : trend === "down" ? "text-red-600" : "text-slate-900"
            }`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TypeStats({ title, icon: Icon, stats, color }) {
  const bgColor = color === "emerald" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200";
  const textColor = color === "emerald" ? "text-emerald-700" : "text-amber-700";
  
  return (
    <div className={`${bgColor} border rounded-lg p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${textColor}`} />
        <h3 className={`font-bold ${textColor}`}>{title}</h3>
        <Badge variant="outline" className="ml-auto">{stats.nb_factures || 0} facturés / {stats.nb_dossiers} total</Badge>
      </div>
      <div className="grid grid-cols-4 gap-3 text-center">
        <div>
          <p className="text-xs text-slate-500">Coûts</p>
          <p className="font-bold text-red-600 text-sm">{formatMoney(stats.total_cout)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Facturé</p>
          <p className="font-bold text-blue-600 text-sm">{formatMoney(stats.total_facture)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Bénéfice</p>
          <p className={`font-bold text-sm ${stats.benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatMoney(stats.benefice)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Marge</p>
          <p className={`font-bold text-sm ${stats.marge_pct >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
            {stats.marge_pct || 0}%
          </p>
        </div>
      </div>
    </div>
  );
}

export default function RentabiliteView() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("semaine");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentWeek, setCurrentWeek] = useState(getWeek(now, { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(now.getMonth() + 1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === "semaine" 
        ? `/rentabilite/semaine?year=${currentYear}&week=${currentWeek}`
        : `/rentabilite/mois?year=${currentYear}&month=${currentMonth}`;
      const response = await api.get(endpoint);
      setData(response.data);
    } catch (error) {
      console.error("Erreur chargement rentabilité:", error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [viewMode, currentYear, currentWeek, currentMonth]);

  const navigatePrev = () => {
    if (viewMode === "semaine") {
      if (currentWeek > 1) setCurrentWeek(currentWeek - 1);
      else { setCurrentYear(currentYear - 1); setCurrentWeek(52); }
    } else {
      if (currentMonth > 1) setCurrentMonth(currentMonth - 1);
      else { setCurrentYear(currentYear - 1); setCurrentMonth(12); }
    }
  };

  const navigateNext = () => {
    if (viewMode === "semaine") {
      if (currentWeek < 52) setCurrentWeek(currentWeek + 1);
      else { setCurrentYear(currentYear + 1); setCurrentWeek(1); }
    } else {
      if (currentMonth < 12) setCurrentMonth(currentMonth + 1);
      else { setCurrentYear(currentYear + 1); setCurrentMonth(1); }
    }
  };

  const totaux = data?.totaux || {};
  const mecaStats = data?.mecanique || { nb_dossiers: 0, nb_factures: 0, total_cout: 0, total_facture: 0, benefice: 0, marge_pct: 0 };
  const carroStats = data?.carrosserie || { nb_dossiers: 0, nb_factures: 0, total_cout: 0, total_facture: 0, benefice: 0, marge_pct: 0 };
  const charges = viewMode === "semaine" ? (data?.charges_hebdo || 0) : (data?.charges_mensuelles || 0);
  const nbSemaines = data?.nb_semaines_mois || 0;

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 animate-fade-in" data-testid="rentabilite-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Calculator className="h-7 w-7 text-slate-700" />
          <h1 className="text-xl md:text-2xl font-black text-slate-900">Rentabilité</h1>
        </div>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="semaine">Semaine</TabsTrigger>
            <TabsTrigger value="mois">Mois</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 bg-white rounded-lg p-3 border border-slate-200">
        <Button variant="outline" size="icon" onClick={navigatePrev}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center min-w-[200px]">
          <p className="text-lg font-bold text-slate-900">
            {viewMode === "semaine" ? `Semaine ${currentWeek}` : MONTHS[currentMonth - 1]}
          </p>
          <p className="text-sm text-slate-500">{currentYear}</p>
          {data?.period_start && data?.period_end && (
            <p className="text-xs text-slate-400 mt-1">
              {new Date(data.period_start).toLocaleDateString('fr-FR')} → {new Date(data.period_end).toLocaleDateString('fr-FR')}
            </p>
          )}
          {viewMode === "semaine" && nbSemaines > 0 && (
            <p className="text-[10px] text-slate-400">Charges ÷ {nbSemaines} sem. ce mois</p>
          )}
        </div>
        <Button variant="outline" size="icon" onClick={navigateNext}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <>
          {/* Résultat Net */}
          <Card className={`border-2 ${totaux.resultat_net >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-600 mb-1">Résultat Net</p>
              <p className={`text-4xl md:text-5xl font-black ${totaux.resultat_net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatMoney(totaux.resultat_net)}
              </p>
              <div className="flex items-center justify-center gap-4 mt-2">
                {totaux.resultat_net >= 0 ? <TrendingUp className="h-5 w-5 text-emerald-500" /> : <TrendingDown className="h-5 w-5 text-red-500" />}
                <span className="text-sm text-slate-500">Charges: {formatMoney(charges)}</span>
                <span className="text-sm font-bold text-indigo-600">Marge: {totaux.marge_pct || 0}%</span>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard title="Dossiers facturés" value={totaux.nb_factures || 0} icon={Receipt} color="slate" />
            <StatCard title="Total Coûts" value={formatMoney(totaux.total_cout)} icon={TrendingDown} color="red" />
            <StatCard title="Total Facturé" value={formatMoney(totaux.total_facture)} icon={DollarSign} color="blue" />
            <StatCard title="Bénéfice Brut" value={formatMoney(totaux.benefice_brut)} icon={TrendingUp} color={totaux.benefice_brut >= 0 ? "emerald" : "red"} trend={totaux.benefice_brut >= 0 ? "up" : "down"} />
            <StatCard title="Marge globale" value={`${totaux.marge_pct || 0}%`} icon={Percent} color="indigo" />
          </div>

          {/* By Type with percentages */}
          <div className="grid md:grid-cols-2 gap-4">
            <TypeStats title="Mécanique" icon={Wrench} stats={mecaStats} color="emerald" />
            <TypeStats title="Carrosserie" icon={Paintbrush} stats={carroStats} color="amber" />
          </div>

          {/* Comparaison contribution au bénéfice méca vs carro */}
          {(mecaStats.benefice > 0 || carroStats.benefice > 0) && (() => {
            const totalBenefice = Math.abs(mecaStats.benefice || 0) + Math.abs(carroStats.benefice || 0);
            const mecaPct = totalBenefice > 0 ? Math.round((mecaStats.benefice / totalBenefice) * 100) : 0;
            const carroPct = totalBenefice > 0 ? Math.round((carroStats.benefice / totalBenefice) * 100) : 0;
            return (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-sm font-bold text-slate-700 mb-3">Répartition du bénéfice</p>
                  <div className="flex h-8 rounded-full overflow-hidden bg-slate-100">
                    {mecaStats.benefice > 0 && (
                      <div className="bg-emerald-500 flex items-center justify-center transition-all" style={{ width: `${mecaPct}%` }}>
                        <span className="text-white text-xs font-bold px-1">{mecaPct}%</span>
                      </div>
                    )}
                    {carroStats.benefice > 0 && (
                      <div className="bg-amber-500 flex items-center justify-center transition-all" style={{ width: `${carroPct}%` }}>
                        <span className="text-white text-xs font-bold px-1">{carroPct}%</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-sm">Mécanique: <strong className="text-emerald-700">{formatMoney(mecaStats.benefice)}</strong> ({mecaPct}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-sm">Carrosserie: <strong className="text-amber-700">{formatMoney(carroStats.benefice)}</strong> ({carroPct}%)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Dossiers List (week view only) */}
          {viewMode === "semaine" && data?.dossiers && data.dossiers.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Dossiers facturés cette semaine</span>
                  {data.en_cours > 0 && <Badge variant="outline" className="text-xs">{data.en_cours} en cours (non comptés)</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-2">Véhicule</th>
                        <th className="text-left py-2 px-2">Client</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-right py-2 px-2">Coût</th>
                        <th className="text-right py-2 px-2">Facturé</th>
                        <th className="text-right py-2 px-2">Bénéfice</th>
                        <th className="text-right py-2 px-2">Marge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dossiers.map((dossier) => {
                        const marge = dossier.montant_facture > 0 ? Math.round((dossier.benefice / dossier.montant_facture) * 100) : 0;
                        return (
                          <tr key={dossier.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/vehicles/${dossier.id}`)}>
                            <td className="py-2 px-2 font-mono font-bold">{dossier.immatriculation}</td>
                            <td className="py-2 px-2">{dossier.client}</td>
                            <td className="py-2 px-2">
                              <Badge variant={dossier.type === "mecanique" ? "default" : "secondary"} className="text-xs">
                                {dossier.type === "mecanique" ? "Méca" : "Carro"}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-right text-red-600">{formatMoney(dossier.cout_revient)}</td>
                            <td className="py-2 px-2 text-right text-blue-600">{formatMoney(dossier.montant_facture)}</td>
                            <td className={`py-2 px-2 text-right font-bold ${dossier.benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatMoney(dossier.benefice)}
                            </td>
                            <td className={`py-2 px-2 text-right font-bold ${marge >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
                              {marge}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
